import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const SITE_LANGUAGE_STORAGE_KEY = "kavia-home-language";
export const SITE_LANGUAGES = Object.freeze([
  { value: "es", label: "ES · Español" },
  { value: "en", label: "EN · English" },
]);

const DEFAULT_LANGUAGE = "en";
const SUPPORTED_LANGUAGES = SITE_LANGUAGES.map(({ value }) => value);
const SiteLanguageContext = createContext(null);

function normalizeLanguage(language) {
  return SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    return normalizeLanguage(window.localStorage.getItem(SITE_LANGUAGE_STORAGE_KEY));
  } catch (_error) {
    return DEFAULT_LANGUAGE;
  }
}

function persistLanguage(language) {
  try {
    window.localStorage.setItem(SITE_LANGUAGE_STORAGE_KEY, language);
  } catch (_error) {
    // The preference remains active for this visit when storage is unavailable.
  }
}

export function SiteLanguageProvider({ children }) {
  const [language, setStoredLanguage] = useState(readStoredLanguage);

  const setLanguage = useCallback((nextLanguage) => {
    setStoredLanguage(normalizeLanguage(nextLanguage));
  }, []);

  useEffect(() => {
    persistLanguage(language);
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <SiteLanguageContext.Provider value={value}>
      {children}
    </SiteLanguageContext.Provider>
  );
}

export function useSiteLanguage() {
  const context = useContext(SiteLanguageContext);

  if (!context) {
    throw new Error("useSiteLanguage must be used within SiteLanguageProvider");
  }

  return context;
}
